package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.BoxSizing.BORDER_BOX
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.TextAlign.CENTER
import com.downdogapp.client.*
import com.downdogapp.client.api.HistoryItem
import com.downdogapp.client.controllers.HistoryItemViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.HistoryUtil.favorite
import com.downdogapp.client.widget.*
import com.downdogapp.client.widget.StructuredRow.Companion.leftIconRow
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSpanElement
import kotlin.math.roundToInt

actual class HistoryItemView actual constructor(
  private val controller: HistoryItemViewController
) : TopLevelView(), View {

  private var actionsTable: TableView
  private var title: HTMLSpanElement
  private var subtitle: HTMLSpanElement

  override var root: HTMLDivElement = controllerView {
    bg = background(true)
    fg = foreground(true) {
      paddingBottom = px(40)
      boxSizing = BORDER_BOX

      centeredColumnView {
        pageHeader(onBackClicked = controller::onBackClicked)

        verticalFlexBox {
          this@HistoryItemView.title = label(text = controller.title, fontSize = 20, textAlign = CENTER) {
            marginLeft = HORIZONTAL_CONTENT_GUTTER_PX
            marginRight = HORIZONTAL_CONTENT_GUTTER_PX
            marginTop = DETAIL_TITLE_TOP_MARGIN_PX
          }

          this@HistoryItemView.subtitle = label(text = controller.subtitle, fontSize = 12, textAlign = CENTER) {
            marginLeft = HORIZONTAL_CONTENT_GUTTER_PX
            marginRight = HORIZONTAL_CONTENT_GUTTER_PX
            marginTop = px(8)
          }

          detailSettingsView(controller.item) {
            marginTop = SETTINGS_TOP_MARGIN_PX
            marginLeft = HORIZONTAL_CONTENT_GUTTER_PX * 4
            marginRight = HORIZONTAL_CONTENT_GUTTER_PX * 4
            marginBottom = px(30)
          }

          actionsTable = tableView(::getContents)

          if (controller.item.displayStart) {
            roundedTextButton(
              text = Strings.START,
              marginHorizontal = HORIZONTAL_CONTENT_GUTTER_PX,
              clickFn = controller::onStartPracticeClicked
            )
          } else if (controller.displayOpenInOtherApp) {
            roundedTextButton(
              text = Strings.OPEN_IN_X(controller.item.appType.displayName),
              marginHorizontal = HORIZONTAL_CONTENT_GUTTER_PX,
              clickFn = controller::openInOtherAppClicked
            ) {
              marginTop = px(8)
            }
          }
        }
      }
    }
  }

  actual override fun refreshView() {
    super.refreshView()
    actionsTable.refreshContents()
    title.text = controller.title
    subtitle.text = controller.subtitle
  }

  private fun getContents() = TableContents(
    rows = listOfNotNull(
      if (controller.item.favorite)
        leftIconRow(
          image = Images.HEART,
          text = Strings.REMOVE_FROM_FAVORITES,
          fontSize = 14,
          clickFn = controller::removeFromFavoritesClicked
        )
      else
        leftIconRow(
          image = Images.HEART,
          text = Strings.FAVORITE,
          fontSize = 14,
          clickFn = controller::addToFavoritesClicked
        ),
      leftIconRow(
        image = Images.SETTINGS_PENCIL,
        text = Strings.EDIT_NAME,
        clickFn = controller::editNameClicked
      ),
      if (controller.item.displaySongs)
        leftIconRow(
          image = Images.SETTINGS_MUSIC,
          text = Strings.SONG_LIST,
          fontSize = 14,
          clickFn = controller::playlistClicked
        )
      else
        null,
      if (controller.showPoseList)
        leftIconRow(
          image = Images.POSE_LIST,
          text = controller.poseListTitle,
          fontSize = 14,
          clickFn = controller::poseListClicked
        )
      else
        null,
      MANIFEST.shareUrl?.let {
        leftIconRow(
          image = Images.SETTINGS_SHARE,
          text = Strings.SHARE_THIS_PRACTICE,
          fontSize = 14,
          clickFn = controller::shareClicked
        )
      },
      leftIconRow(
        image = Images.SETTINGS_TRASH,
        text = Strings.REMOVE_FROM_HISTORY,
        fontSize = 14,
        clickFn = controller::removeFromHistoryClicked
      )
    ),
    separator = rightSeparator
  )

  companion object {

    // All of these are used in both HistoryItemView and SavedPracticeView
    val DETAIL_TITLE_TOP_MARGIN_PX = px(20)
    val SETTINGS_TOP_MARGIN_PX = px(32)

    fun HTMLElement.detailSettingsView(item: HistoryItem,
                                       block: HTMLDivElement.() -> Unit): HTMLDivElement =
      div {
        lateinit var currentRow: HTMLDivElement
        for ((index, text) in item.settingTexts.withIndex()) {
          if (index % 2 == 0) {
            currentRow = horizontalFlexBox()
          }
          currentRow.detailSetting(text)
        }
        val parent =
          if (item.settingTexts.size % 2 == 0)
            horizontalFlexBox()
          else
            currentRow

        parent.detailSetting(
          Strings.X_PERCENT_COMPLETE((item.proportionCompleted * 100).roundToInt())
        )

        block()
      }

    private fun HTMLDivElement.detailSetting(text: String) =
      horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
        width = percent(50)
        marginTop = px(5)
        marginBottom = px(20)

        label(text = text, fontSize = 15)
      }
  }
}
